import { Ref, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Form, InputGroup, Table, Alert, Card, Tooltip, OverlayTrigger, Dropdown, Modal } from "react-bootstrap";
import InfiniteScroll from "react-infinite-scroll-component";
import { lddGlobalSessionInfo, NavObjectType, ProvincesShortCode, ScreenActions } from "../navigationTypes";
import { createFile, createFileFromPCLaw, createFileFromTemplate, FileListColumn, IAdSearchValue } from "../fileList/fileList";
import AdvancedSearchBox from "../fileList/AdvancedSearchBox";
import ClioIntegration from "../clio/Clio";
import { debounce } from "../../lib/debounce";
import "./List.css";
import { useAppSelector } from "../../redux/hooks";
import { NavigationContext } from "../../Context/NavigationContext";
import { searchBarPlaceholder, thirdPartyNavigateObjs } from "./ListTypes";
import { getDataReader, RPWDataReader } from "../../livedataFuncs";
import { ThirdPartyDispatch } from "../ThirdPartyList/ThirdPartyFuncs";
import { useDispatch } from "react-redux";
import { runInnerFunction } from "../../compatManager";
import { lddStateObject, setCreateFileFromAPS, setListDataState } from "../../redux/reducer";
import { BuildThirdPartyRecommendationsLink, handleSetData, ListExtraData } from './ListFuncs';
import React from "react";
import type { JSX } from 'react';
import { MortgageLenderItem } from "../MortgageLender/MortgageLenderTypes";
import { isPageInDR } from "../../dr/Components/drFuncs";
import { displayToastMsg } from "../toastMessages/toastmessages";
import { getAPSDisplayAbbr } from "../../fileToolbar/RenAI/renaiFuncs";
import RealtorSearchModal from "./RealtorSearchModal";

export enum SortDir {
    Up = "up",
    Down = "down",
    None = "none"
}

export type ListName = "FileList" |
    "LawyerList" |
    'NotaryList' |
    "AllLawyerList" |
    "InsuranceList" |
    "RealEstateBrokerList" |
    "PurchaserRealEstateBrokerList" |
    "ConveyancerList" |
    'MortgageBrokerList' |
    'MortgageLenderList' |
    'AllMortgageLenderList' |
    'MortgageBranchList' |
    'MortgageLenderSelectFieldList' |
    'DischargeLenderList' |
    'OtherPartyList' |
    'SelectFileList' |
    'DRFileList' |
    'SuggestedList' | 
    'TaskList';

export type ListColumn = {
    attrName: string;
    dataType: string;
    width?: string;
    title: string;
}

interface Props<T> extends IList, IFileList<T>, IThirdpartyList<T> {
    listName: ListName;
    listContainerID?: string;
    as?: "Card" | "ModalBody";
    ListColumns?: string[];
    defaultListColumns?: ListColumn[];
    nodeName?: string;
    searchTable?: string;
    notShowEndMessage?: boolean;
    headBar?: (...arg: any) => JSX.Element;
    filter?: (...arg: any) => JSX.Element;
    buildHead?: (...arg: any[]) => JSX.Element;
    buildListLink?: (columns: ListColumn[], searchText?: string) => any;
    createNewFile?: () => void;
    dataType?: "static" | "dynamic";
    buildSearchFilters?: (...arg: any[]) => JSX.Element;
    staticSearch?: (data: any[], searchText: string) => any[];
    filterCount?: number;
    showFilters?: boolean;
    setShowFilters?: (show: boolean) => void;
    filterComponent?: any;
    setFilterComponent?: (filter: any) => any;
    branchEntry?: MortgageLenderItem;
    defaultColumn?: string;
    buildButtonRight?: () => JSX.Element;
    sortingMethod?: "client" | "server";
    viewSettings?: () => void;
    handleDefaultData?: (response: Promise<Response>[]) => void;
}

interface IList {
    handleLoadData?: (firmId: number, sid: string) => Promise<Response>[];
}

interface IFileList<T> {
    handleLoadFileData?: (firmId: number, prov: ProvincesShortCode, sid: string, filePage: number, searchText: string, adSearch: IAdSearchValue, setADSearch: (val: IAdSearchValue) => void, sortedColumn?: string, sortingOrder?: boolean) => Promise<Response>[];
    FileListColumns?: FileListColumn[];
    showCopyOption?: boolean,
    setShowCopyOption?: (val: boolean) => void,
    setCopyAppID?: (val: number) => void,
    archiveFile?: (firmId: number, prov: ProvincesShortCode, sid: string, appId: number, toArchive: boolean, callback: () => void) => void;
    buildFileListLink?: (columns: FileListColumn[], nav?: (frame: NavObjectType) => void, showCopyOption?: boolean, setShowCopyOption?: (val: boolean) => void, setCopyAppID?: (val: number) => void, highlightText?: string, isArchivedList?: boolean, archiveFileHandler?: (appId: number) => void) => ((T: T) => JSX.Element);
}

interface IThirdpartyList<T> {
    handleLoadQpickData?:
    (firmId: number,
        prov: ProvincesShortCode,
        sid: string,
        filePage: number,
        searchText: string,
        searchTable: string,
        ListExtraData: ListExtraData) => Promise<Response>[]
    buildQpickListLink?: (columns: string[], dataroot: Element, tableType: string, navObj?: NavObjectType, navigateContext?: (navObject: NavObjectType, init?: boolean) => void, province?: ProvincesShortCode, searchString?: string, dispatch?: React.Dispatch<any>, otherListExtraPayload?: any, skipSetThirdPartyData?: boolean) => ((T) => JSX.Element);
    buildLenderListLink?: (...arg: any[]) => ((T: T, index?: number) => JSX.Element);
    setLenderName?: (string) => void,
    closeFunc?: (boolean) => void,
    fieldType?: string,
    extraData?: string[]
}


const bottomEnabled: { [key: string]: boolean } = {
    "AllLawyerList": true,
    "NotaryList": true,
    "LawyerList": true,
}

const List = forwardRef(<T,>(props: Props<T>, ref: Ref<{ staticSearch: () => void, reloadData: () => void }>) => {
    const nav = useContext(NavigationContext);
    const dispatch = useDispatch();
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const data = useAppSelector(g => g.ListData[props.listName]);
    const [freesearchData, setFreesearchData] = useState<T[]>([]);
    const [dataPage, setDataPage] = useState<number>(1);
    const [hasMoreData, setHasMoreData] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string>("");
    const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(false);
    const [showRealtorModal, setShowRealtorModal] = useState(false);
    const [isClioActivated, setIsClioActivated] = useState<boolean>(false);
    const [showClioModal, setShowClioModal] = useState<boolean>(false);
    const [adSearch, setADSearch] = useState<IAdSearchValue>({});
    const [sortingOrder, setSortingOrder] = useState<boolean>(false);
    const [sortedColumn, setSortedColumn] = useState<string>(props.defaultColumn ?? '');
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const otherListExtraPayload = useAppSelector((g: lddStateObject) => g.ThirdPartyListExtraPayload);
    const [filterDataState, setFilterDataState] = useState(['', '', '', '']);
    const abortController = useRef<AbortController>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [initLoad, setInitLoad] = useState(0);
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [searchTextHolder, setSearchTextHolder] = useState<string>("");
    const [showSearchRecommendations, setShowSearchRecommendations] = useState(false);
    const [showBottomMessage, setShowBottomMessage] = useState(false);
    const page = useAppSelector((state) => state.currentNavigation?.page);
    const [sortDirections, setSortDirections] = useState<{[key: string]: SortDir}>({});
    const skipSetThirdPartyData = useAppSelector((state: lddStateObject) => state.SkipSetThirdPartyData);
    const APSDisplayAbbr = getAPSDisplayAbbr(SESSION_INFO.appProvinceShort);

    function setData(newData: T[]) {
        dispatch(setListDataState(props.listName, newData));
    }
    function handleLoadData(
        dataPage: number,
        searchText: string,
        adSearch?: IAdSearchValue,
        setADSearch?: React.Dispatch<React.SetStateAction<IAdSearchValue>>,
        searchTable?: string,
        sortedColumn?: string,
        sortingOrder?: boolean,
        extraData?: string[],
        signal?: AbortSignal,
        suggestions?: boolean
    ): Promise<Response>[] {
        const sortingObj = [sortedColumn, sortingOrder];

        const listExtraData: ListExtraData = {
            extraQueryFilter: otherListExtraPayload,
            extraSearchData: extraData,
            mortgageBranchEntry: props?.branchEntry,
            sortingObj: sortingObj,
            signal: signal,
            suggestions: suggestions,
        }

        switch (props.listName) {
            case "FileList":
            case "SelectFileList":
            case "DRFileList":
            case "TaskList":
                const response = props.handleLoadFileData(SESSION_INFO.firm.id, SESSION_INFO.appProvinceShort, SESSION_INFO.sid, dataPage, searchText, adSearch, setADSearch, sortedColumn, sortingOrder);
                if(props.handleDefaultData){
                    props.handleDefaultData(response);
                }
                return response;
            case "MortgageBranchList":
                return props.handleLoadQpickData(SESSION_INFO.firm.id, SESSION_INFO.appProvinceShort, SESSION_INFO.sid, dataPage, searchText, searchTable, listExtraData);
            default:
                return props.handleLoadQpickData(SESSION_INFO.firm.id, SESSION_INFO.appProvinceShort, SESSION_INFO.sid, dataPage, searchText, searchTable, listExtraData);
        }
    }
    useEffect(() => {
        abortController.current = new AbortController();

        setDataPage(1);
        setFreesearchData([]);
        setDefaultFilters();
        CheckClioActivation();
    }, []);


    useEffect(() => {
        if (initLoad == 1) {
            setInitLoad(2);
            const responsePromises = handleLoadData(dataPage, searchText, adSearch, setADSearch, props.searchTable, sortedColumn, false, filterDataState);
            handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData, null, dataPage, setDataPage);
        }
    }, [initLoad]);

    function setDefaultFilters() {
        //might be a problem if there are a lot of user lawyer entries. 
        var filterState = ['', '', '', ''];
        if (initLoad == 0) {
            if (props.listName == "AllLawyerList") {
                if (SESSION_INFO.appProvinceShort !== 'BC') {
                    const responsePromises = handleLoadData(1, '', {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, ['User'], null);
                    responsePromises[0]?.then((data) => {
                        data.json().then((data) => {
                            if (data.result.length > 50) {
                                filterState = ['', 'User', '', ''];
                            } else {
                                filterState = ['System', 'User', '', ''];   
                            }
                            setFilterDataState(filterState);
                            setInitLoad(1);
                        });
                    });
                } else {
                    setInitLoad(1);
                }
            } else {
                setInitLoad(1);
            }
        }
    }

    useEffect(() => {
        setRecommendations([]);
        if (searchText?.length > 1 && filterDataState[1] == 'User' && filterDataState[0] == '') {
            const responsePromises = handleLoadData(1, searchText, {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, ['System'], null, true);
            handleSetData("SuggestedList", responsePromises, setRecommendations, null, null);
            setShowSearchRecommendations(true);
        } else {
            setShowSearchRecommendations(false);
        }
        if (searchText === '' && props.listName === "TaskList") {
            staticSearch();
        }
    }, [searchText, filterDataState]);

    function handleArchiveFile(appId: number) {
        const fileIndex = data.findIndex(file => file["AppID"] === appId);
        props.archiveFile(
            SESSION_INFO.firm.id,
            SESSION_INFO.appProvinceShort,
            SESSION_INFO.sid,
            appId,
            data[fileIndex]["Archived"] === null || data[fileIndex]["Archived"] !== "1",
            () => {
                data[fileIndex]["Archived"] = data[fileIndex]["Archived"] === null || data[fileIndex]["Archived"] !== "1" ? "1" : "0";
                setData([...data]);
            })
    }

    function handleColumnClick(column: string) {
        if (isLoading && abortController.current) {
            abortController.current.abort("Loading new sort");
        }

        abortController.current = new AbortController();

        setHasMoreData(true);
        setDataPage(2);
        setFreesearchData([]);
        setIsLoading(true);
        setSearchTextHolder("");
        setSearchText("");

        const newSortDirections = { ...sortDirections };
        Object.keys(newSortDirections).forEach(key => {
            if (key !== column) newSortDirections[key] = SortDir.None;
        });
        
        newSortDirections[column] = sortDirections[column] === SortDir.Up ? SortDir.Down : SortDir.Up;
        if (!sortDirections[column]) newSortDirections[column] = SortDir.Up;
        
        setSortDirections(newSortDirections);
        setSortingOrder(newSortDirections[column] === SortDir.Up);
        setSortedColumn(column);

        if (props.dataType === "static") {
            debounce(() => {
                setFreesearchData(freesearchData.sort((a, b) => {
                    const aVal = a[column];
                    const bVal = b[column];
                    if (!aVal !== !bVal) {
                        return !aVal ? (sortingOrder ? 1 : -1) : (sortingOrder ? -1 : 1);
                    }
                    if (!aVal && !bVal) return 0;
                    if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return sortingOrder ? aVal - bVal : bVal - aVal;
                    }
                    return sortingOrder 
                        ? String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase())
                        : String(bVal).toLowerCase().localeCompare(String(aVal).toLowerCase());
                }));
                setHasMoreData(false);
            }, 500)
        } 
    }

    useEffect(() => {   
        if (initLoad == 2) {
            if (props?.sortingMethod == "client") {
                data.sort((a, b) => {
                    let aVal = a[sortedColumn] ?? '';
                    let bVal = b[sortedColumn] ?? '';
                    return (aVal.localeCompare(bVal)) * (!sortingOrder ? 1 : -1);
                });
                setData(data);
                setHasMoreData(false);
            } else {
                try {
                    const responsePromises = handleLoadData(1, searchText, adSearch, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState, abortController.current.signal);
                    handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
                } catch (e) {
                    if (e.name === 'AbortError') {
                        return;
                    } else {
                        throw e;
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        }
    }, [sortingOrder, sortedColumn]);

    function setFilterClick(filters: string[]) {

        if (isLoading && abortController.current) {
            abortController.current.abort("Filter Load");
            return;
        }

        abortController.current = new AbortController();
        setShowBottomMessage(false);
        setHasMoreData(true);
        setDataPage(1);
        setFreesearchData([]);
        setFilterDataState(filters);
        setIsLoading(true);
    }

    useEffect(() => {    
        if (initLoad == 2) {
            try {
                const responsePromises = handleLoadData(1, searchText, {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState, abortController.current.signal);
                setData([]);
                handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData, null);
            } catch (error) {
                if (error.name === "AbortError") {
                    return;
                }
            } finally {
                setIsLoading(false);
                setDataPage(2);
            }
        }
    }, [filterDataState]);

    useEffect(() => {
        if (props.branchEntry != null && props.listName == "MortgageBranchList") {
            setHasMoreData(true);
            setDataPage(1);
            setFreesearchData([]);
            setSearchText("");

            const responsePromises = handleLoadData(1, "", {}, setADSearch, props.searchTable, sortedColumn, sortingOrder);
            
            handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);

        }
    }, [props.branchEntry]);


    let searchButtonString;
    var isBC = false;
    switch (SESSION_INFO.appProvinceShort) {
        case 'BC':
            searchButtonString = 'Law Society/Society of Notaries';
            isBC = true;
            break;
        case 'NS':
            searchButtonString = "Barristers' Society";
            break;
        case 'AB':
        case 'ON':
        case 'MB':
        case 'SK':
        default:
            searchButtonString = 'Law Society';
            break;
    }
    
    useImperativeHandle(ref, () => (ref ? {
        staticSearch,
        reloadData,
    } : null));

    const createFileFromAPS = (nav: (frame: NavObjectType) => void) => {
        try{
            dispatch(setCreateFileFromAPS(true));
        } catch (error) {
            displayToastMsg({ id: null, msg: "Failed to create file from APS", type: 'error' }, null);
        }
    }

    const staticSearch = () => {
        if (data && data.length && initLoad == 2) {
            setHasMoreData(true); // reset loading msg
            debounce(() => {
                let filteredData = data;
                filteredData = props.staticSearch(data, searchText.toLowerCase());
                
                setFreesearchData(filteredData);
                setHasMoreData(false);
            }, 500);
        }
    }

    const reloadData = () => {
        setDataPage(1);
        setFreesearchData([]);
        const responsePromises = handleLoadData(1, searchText, adSearch, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState);
        handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
        staticSearch();
    }

    const searchBarHandler = (e) => {
        if (props.dataType === "static") {
            setHasMoreData(true); // reset loading msg
            setSearchText(e.target.value);
            debounce(() => {
                let filteredData = data;
                if (e.target.value > "") {
                    filteredData = props.staticSearch(data, e.target.value.toLowerCase());
                }
                setFreesearchData(filteredData);
                setHasMoreData(false);
            }, 500);
        } else {
            setHasMoreData(true); // reset loading msg
            setDataPage(2);
            setSearchText(e.target.value);
            setFreesearchData([]);
            setADSearch({});
            debounce(() => {
                const responsePromises = handleLoadData(1, e.target.value, {}, setADSearch, props.searchTable, sortedColumn, sortingOrder);
                handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
            }, 500);
        }
    }

    const CheckClioActivation = () => {
        const checkBetaAccess = async () => {
            try {
                const betaUserRes = await fetch("js/BetaUser.json", { 
                    method: 'GET', 
                    cache: "default" 
                });
                
                if (betaUserRes.status === 200) {
                    const betaUser = await betaUserRes.json();
                    const clioBetaUsers = betaUser.Clio || [];
                    setIsClioActivated(clioBetaUsers.includes(SESSION_INFO.firm.id.toString()));
                }
            } catch (e) {
                console.error('Error checking beta access:', e);
                setIsClioActivated(false);
            }
        };

        checkBetaAccess();
    }

    const HeadPart = () => { 
        const handleManualEntry = () => {
            runInnerFunction('ReactShell.reactCleanThirdParty', { 
                thirdParty: props.listName, 
                type: otherListExtraPayload?.filterType ? otherListExtraPayload?.filterType : undefined 
            });
            ThirdPartyDispatch(dispatch, props.listName, false);
            const navObj = { ...thirdPartyNavigateObjs[props.listName] };
            const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
            navObj.params.id = DataReaderObj.getAppId();
            navObj.params.PSM = DataReaderObj.getPSM();
            navObj.controller = { ...navObj.controller };
            navObj.controller.tabNumber = undefined;
            if (otherListExtraPayload && otherListExtraPayload?.filterType) {
                switch (props.listName) {
                    case "OtherPartyList":
                        navObj.controller.screenAction = ScreenActions.setOtherPartyType;
                        navObj.controller.onClickDispatchPayload = { value: otherListExtraPayload?.filterType }
                        break;
                }
            }
            nav(navObj);
        };

        const handleRealtorFromSearch = async (realtor: {
            name?: string;
            title?: string;
            officeName?: string;
            officeAddress?: string;
            contactNumber?: string;     
            firmPhone?: string;         
            website?: string;
            qpickrepository_id?: string | number; 
            email?: string;
            officeEmail?: string;
            fax?: string;

            }) => {
            const root = livedataRootContainer?.dataRoot as Element | null;
            if (!root) return;

            const doc = root.ownerDocument || root.parentNode?.ownerDocument;
            if (!doc) return;

            const trim = (s?: string) => (s ?? "").trim();

            const isPurchaser = props.listName;
            const nodeTag = isPurchaser === "PurchaserRealEstateBrokerList" ? "PurchaserRealEstateAgent" : "RealEstateAgent";
            Array.from(root.getElementsByTagName(nodeTag)).forEach(n => root.removeChild(n));

            const agentNode = doc.createElement(nodeTag);

            const office = trim(realtor.officeName);
            const agentName = trim(realtor.name);

            agentNode.setAttribute("qpickrepository_id", String(realtor.qpickrepository_id ?? ""));
            agentNode.setAttribute("type", "RealEstateAgent");
            agentNode.setAttribute("indexattributevalue", office);

            agentNode.setAttribute("Name", agentName);
            agentNode.setAttribute("Title", trim(realtor.title));
            agentNode.setAttribute("Phone", trim(realtor.contactNumber));
            agentNode.setAttribute("Website", trim(realtor.website));

            agentNode.setAttribute("CompanyAgentName", office && agentName ? `${office} - ${agentName}` : "");
            agentNode.setAttribute("Address", trim(realtor.officeAddress));
            agentNode.setAttribute("Company", office);
            agentNode.setAttribute("FirmPhone", trim(realtor.firmPhone));

            agentNode.setAttribute("Email", trim(realtor.email));
            agentNode.setAttribute("OfficeEmail", trim(realtor.officeEmail));
            agentNode.setAttribute("Fax", trim(realtor.fax));

            root.appendChild(agentNode);

            setShowRealtorModal(false);
            ThirdPartyDispatch(dispatch, props.listName, false);

            const navObj = { ...thirdPartyNavigateObjs[props.listName] };
            const DataReaderObj: RPWDataReader = getDataReader(root);
            navObj.params.id = DataReaderObj.getAppId();
            navObj.params.PSM = DataReaderObj.getPSM();
            navObj.controller = { ...navObj.controller };
            navObj.controller.tabNumber = undefined;
            nav(navObj);
        };
        
        return (
        props.headBar ?
            <>
                <props.headBar filterFunc={setFilterClick} filterState={filterDataState} setFilterState={setFilterDataState}>
                    <InputGroup.Text>
                        <i style={{ cursor: "default" }} className="fa-duotone fa-magnifying-glass fa-indicator"></i>
                    </InputGroup.Text>
                    <Form.Control id="fileSearchInput" placeholder={`type to search for a ${ isBC ? `legal representative..` : searchBarPlaceholder[props.listName]}..`} autoComplete={"off"} className="form-search-bar"
                        value={searchTextHolder}
                        onChange={(e) => {
                            setHasMoreData(true); // reset loading msg
                            setDataPage(2);
                            setFreesearchData([]);
                            setSearchTextHolder(e.target.value);
                            setADSearch({});
                            debounce(() => {
                                if (e.target.value.trim().length > 1) {
                                    setSearchText(e.target.value.trim());
                                    setData([]);
                                    const responsePromises = handleLoadData(1, e.target.value.trim(), {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState);
                                    handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
                                } else if (e.target.value.length === 0) {
                                    setSearchTextHolder('');
                                    setSearchText(''); 
                                    setADSearch({});
                                    setData([]);
                                    const responsePromises = handleLoadData(1, '', {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState);
                                    handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
                                }
                            }, 1000);
                        }}
                    >

                    </Form.Control>

                    {searchTextHolder > "" &&
                        <OverlayTrigger placement="bottom" overlay={<Tooltip>Clear search</Tooltip>}>
                            <Button variant="link" id="clearSearchBtn" onClick={() => {
                                    if (searchTextHolder !== '') {
                                        setHasMoreData(true); // reset loading msg
                                        setDataPage(2);
                                        setSearchTextHolder('');
                                        setSearchText('');
                                        setADSearch({});
                                        debounce(() => {
                                            const responsePromises = handleLoadData(1, '', {}, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState);
                                            handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
                                        }, 100)
                                    }
                                }
                            }>
                                <i id="clearSearchBtn2" className="fa-regular fa-xmark"></i>
                            </Button>
                        </OverlayTrigger>}
                </props.headBar>
            </> :
            <>

                {props.listName && (props.listName === 'FileList' || props.listName === 'DRFileList') && <h2>Files</h2>}
                {props.listName && props.listName === 'TaskList' && <h2>Task Preferences</h2>}
                <InputGroup id="searchInputCol" className="search-bar">
                    <InputGroup.Text>
                        <i style={{ cursor: "default" }} className="fa-duotone fa-magnifying-glass fa-indicator"></i>
                    </InputGroup.Text>
                    <Form.Control id="fileSearchInput" placeholder={`type to search for a ${searchBarPlaceholder[props.listName]}..`} autoComplete={"off"}
                        value={searchText}
                        onChange={searchBarHandler}
                    >
                    </Form.Control>

                    <div className="buttons-container">
                        {searchText > "" &&
                            <OverlayTrigger placement="bottom" overlay={<Tooltip>Clear search</Tooltip>}>
                                <Button variant="link" id="clearSearchBtn" onClick={() => {
                                    if (searchText !== '') {
                                        setHasMoreData(true); // reset loading msg
                                        setDataPage(2);
                                        setSearchTextHolder("");
                                        setSearchText('');
                                        setADSearch({});
                                        debounce(() => {
                                            const responsePromises = handleLoadData(1, '', {}, setADSearch, props.searchTable, sortedColumn, sortingOrder);
                                            handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData);
                                        }, 100)
                                    }
                                }}>
                                    <i id="clearSearchBtn2" className="fa-regular fa-xmark"></i>
                                </Button>
                            </OverlayTrigger>
                        }
                        {props.listName === "FileList" &&
                            <OverlayTrigger placement="bottom" overlay={<Tooltip>Advanced Search</Tooltip>}>
                                <Button variant="link" id="searchOptionBtn" onClick={() => {
                                    setShowAdvancedSearch(!showAdvancedSearch);
                                }}>
                                    <i id="searchOptionBtn2" className="fa-regular fa-sliders"></i>
                                </Button>
                            </OverlayTrigger>
                        }
                        {props.listName === "DRFileList" &&
                        <>
                            <Button variant="link" id="searchOptionBtn" onClick={() => {
                                props.setShowFilters(!props.showFilters);
                            }}>
                                <i id="searchOptionBtn2" className="fa-regular fa-bars-filter"></i>
                                Filters {props.filterCount > 0 && `(${props.filterCount})`}
                            </Button>
                        </>
                        }
                        {props.listName === "TaskList" &&
                        <>
                            <Button variant="link" id="searchOptionBtn" onClick={() => {
                                props.setShowFilters(!props.showFilters);
                            }}>
                                <i id="searchOptionBtn2" className="fa-regular fa-sliders"></i>
                                Filters{props.filterCount > 0 && ` (${props.filterCount})`}
                            </Button>
                            <div className="vertical-divider"></div>
                            <div className="switch-container">
                                <Form.Check 
                                    type="switch"
                                    id="show-inactive-switch"
                                    onChange={(e) => {
                                        props.setFilterComponent({
                                            ...props.filterComponent,
                                            Activated: e.target.checked
                                        });
                                    }}
                                />
                            </div>
                            <span className="switch-text">Show Inactive</span>
                        </>
                        }
                    </div>
                </InputGroup>

                {props.listName === 'LawyerList' &&
                    <Button onClick={
                        () => {
                            const navObj = { ...thirdPartyNavigateObjs[props.listName] };
                            const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
                            navObj.params.id = DataReaderObj.getAppId();
                            navObj.params.PSM = DataReaderObj.getPSM();
                            navObj.controller = { ...navObj.controller };
                            // close current modal
                            ThirdPartyDispatch(dispatch, props.listName, false);
                            // condition navObj. 
                            navObj.controller.tabName = "Basic";
                            navObj.controller.autoClickClassName = "#reactOtherSideLawyer";
                            navObj.controller.autoClickClassNamePosition = 0;
                            navObj.controller.tabNumber = 0;
                            // nav to law society popup
                            nav(navObj);
                        }
                    }><i className="fa-regular fa-magnifying-glass"></i> {searchButtonString}</Button>
                }

                {[
                    "InsuranceList",
                    "RealEstateBrokerList",
                    "ConveyancerList",
                    "MortgageBrokerList",
                    "PurchaserRealEstateBrokerList",
                    "OtherPartyList"
                ].indexOf(props.listName) !== -1 && (
                    ["RealEstateBrokerList", "PurchaserRealEstateBrokerList"].includes(props.listName) ? (
        
                        <Dropdown
                            align="end"
                            onClick={(e) => e.stopPropagation()}   // avoid closing the parent modal
                        >
                            <Dropdown.Toggle variant="primary" id="realtor-plus-dropdown">
                                <i className="fa-regular fa-plus"></i>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item
                                    onClick={() => {
                                        setShowRealtorModal(true);
                                    }}
                                >
                                    <i className="fa-regular fa-magnifying-glass"></i> Realtor.ca
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={handleManualEntry}
                                >
                                    <i className="fa-regular fa-pen"></i> Manual Entry
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        // All other lists keep the old single plus behaviour
                        <Button variant="primary" onClick={handleManualEntry}>
                            <i className="fa-regular fa-plus"></i>
                        </Button>
                    )
                )}

                {showRealtorModal && (
                    <RealtorSearchModal
                        show={showRealtorModal}
                        onHide={() => setShowRealtorModal(false)}
                        defaultProvince={SESSION_INFO.appProvinceShort}
                        onSelectRealtor={handleRealtorFromSearch}
                    />
                )}


                {props.listName === "FileList" &&
                    <>
                            <Dropdown onClick={(e) => { e.stopPropagation(); }} align="end">
                                <OverlayTrigger delay={{ show: 350, hide: 400 }} placement="bottom" overlay={<Tooltip>Create new file</Tooltip>}>
                                    <Dropdown.Toggle variant="primary">
                                        <i className="fa-regular fa-plus" ></i> File
                                    </Dropdown.Toggle>
                                </OverlayTrigger>

                                <Dropdown.Menu>
                                    <Dropdown.Header>Create</Dropdown.Header>
                                    <Dropdown.Item onClick={() => createFileFromAPS(nav)}>
                                        <i 
                                            className="fa-solid fa-stars new-file-star-icon"
                                        ></i>
                                            {`New File from ${APSDisplayAbbr}`}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => createFile(nav)}>
                                        <i className="fa-light fa-file"></i>
                                            New File
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => createFileFromTemplate(nav)}>
                                        <i className="fa-light fa-files"></i>
                                            New File from Template
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => createFileFromPCLaw(nav)}>
                                        <i className="fa-light fa-file-import"></i>
                                            PCLaw Import
                                    </Dropdown.Item>
                                    {isClioActivated &&
                                        <Dropdown.Item onClick={() => setShowClioModal(true)}>
                                            <i className="fa-light fa-file-import"></i>
                                                Clio Import
                                        </Dropdown.Item>
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                    </>
                }

                {
                    props.listName === 'TaskList' &&
                    <>
                        <Button variant="primary" onClick={props.createNewFile}>
                            <i className="fa-regular fa-plus" ></i>
                            Task
                        </Button>
                    </>
                }

                {props.listName === "SelectFileList" &&
                    <>
                        <OverlayTrigger placement="bottom" overlay={<Tooltip>Create new file</Tooltip>}>
                            <Button variant="primary" onClick={props.createNewFile}><i className="fa-light fa-plus"></i></Button>
                        </OverlayTrigger>
                    </>
                }

                {props.buildButtonRight && props.buildButtonRight()}

                {showAdvancedSearch && props.listName === "FileList" &&
                    <AdvancedSearchBox
                        listName={props.listName}
                        setData={setData}
                        setDataPage={setDataPage}
                        setHasMoreFiles={setHasMoreData}
                        setSearchQuery={setSearchText}
                        showSearchBox={setShowAdvancedSearch}
                        setShowADSearch={setShowAdvancedSearch}
                        setFreesearchData={setFreesearchData}
                        handleLoadData={props.handleLoadFileData}
                        adSearch={adSearch}
                        setADSearch={setADSearch}
                    />
                }

                {showClioModal &&
                    <ClioIntegration 
                        showModal={showClioModal} 
                        onHideModal={() => setShowClioModal(false)} 
                    />
                }

            {props.showFilters && props.buildSearchFilters()}
        </>
        );
    }

    const EndOfSearchResults = (): JSX.Element => {
        let returnText;
        const navOnClickHandler = () => {
            const navObj = { ...thirdPartyNavigateObjs[props.listName] };
            const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
            navObj.params.id = DataReaderObj.getAppId();
            navObj.params.PSM = DataReaderObj.getPSM();
            navObj.controller = { ...navObj.controller };
            // close current modal
            ThirdPartyDispatch(dispatch, props.listName, false);
            // condition navObj. 
            navObj.controller.autoClickClassName = "#webappOtherSideLawyer";
            navObj.controller.autoClickClassNamePosition = 0;
            navObj.controller.tabName = "Basic";
            navObj.controller.tabNumber = 0;
            // nav to law society popup
            nav(navObj);
        }

        const navOnClickHandlerAddYourOwn = () => {
            const navObj = { ...thirdPartyNavigateObjs[props.listName] };
            const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
            navObj.params.id = DataReaderObj.getAppId();
            navObj.params.PSM = DataReaderObj.getPSM();
            navObj.controller = { ...navObj.controller };
            // close current modal
            ThirdPartyDispatch(dispatch, props.listName, false);
            // condition navObj. 
            navObj.controller.autoClickClassName = "#reactOtherSideLawyerEdit";
            navObj.controller.autoClickClassNamePosition = 0;
            navObj.controller.tabName = "Basic";
            navObj.controller.tabNumber = 1;
            // nav to law society popup
            nav(navObj);
        }

        // make the bottom message sticky
        let stickyStyle : any = {
            position: 'sticky', 
            bottom: '0', 
            width: '100%' 
        };

        let additionalElements: JSX.Element[] = [];
        switch (props.listName) {
            case 'TaskList':
                if (data.length > 0 && freesearchData.length === 0) {
                    returnText = <>No Tasks Found</>;
                } else {
                    returnText = <>End of Results</>;
                }
                break;
            case 'AllLawyerList':
                var extraStrings : any[] = [];

                if (SESSION_INFO.appProvinceShort == 'BC') {
                    extraStrings[0] = `/Notary`;
                    extraStrings[1] = <><br />or the <a onClick={navOnClickHandler}><u>BC Notaries Association</u></a></>;
                } else {
                    extraStrings[0] = "";
                    extraStrings[1] = "";
                }

                const returnMessageA = <><b>Legal Representative not found?</b> Try adjusting your filters, searching on the <a onClick={navOnClickHandler}><u>Law Society of {SESSION_INFO.appProvince}</u></a>{extraStrings[1]} or <a onClick={navOnClickHandlerAddYourOwn}><u>add your own.</u></a></>;
                const returnMessageB = returnText = <><b>Legal Representative not found?</b> Search on the <a onClick={navOnClickHandler}><u>Law Society of {SESSION_INFO.appProvince}</u></a>{extraStrings[1]} or <a onClick={navOnClickHandlerAddYourOwn}><u>add your own.</u></a></>;
                const endOfResults = <>End of Results</>;
                
                if (showBottomMessage) {
                    returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                } else if (searchText.length > 0) {
                    if (filterDataState[0] === '' && filterDataState[1] === 'User') {
                        if (data.length > 0 && recommendations.length > 0) {
                            returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                        } else if (data.length > 0 && recommendations.length == 0) {
                            returnText = skipSetThirdPartyData ? endOfResults : returnMessageB;
                        } else {
                            additionalElements.push(<><div className="infinite-scroll-alert white"><i>No results found in your list.</i></div></>)
                            returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                        }
                    } else {
                        if (data.length > 0) {
                            returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                        } else {
                            returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                        }
                    } 
                } else {
                    if (filterDataState[0] == '' && filterDataState[1] == 'User') {
                        returnText = skipSetThirdPartyData ? endOfResults : returnMessageA;
                    } else {
                        returnText = endOfResults;    
                    }
                }
                break;
            default:
                if (data.length > 0 && freesearchData.length > 0) {
                    returnText = <>End of Results</>;
                } else {
                    returnText = <>No Search Results Found</>;
                }
                if (data.length > 0 && ((!isPageInDR(page) && props.listName !== 'DRFileList') || freesearchData.length > 0)) {
                    returnText = <>End of Results</>;
                }
                break;
        }
        return <>{additionalElements.length > 0 && additionalElements.map(element => element)} <div style={showBottomMessage ? stickyStyle: {}} className="infinite-scroll-alert"> {returnText} </div></>;
    }
    
    const BodyPart = () => (
        <>
            <InfiniteScroll
                dataLength={data.length}
                scrollThreshold={0.7}
                next={() => {
                    if (isLoading)
                        return;

                    setIsLoading(true);

                    if (!showBottomMessage && bottomEnabled[props.listName]) {
                        setShowBottomMessage(true); 
                    }

                    try {
                        const responsePromises = handleLoadData(dataPage, searchText, adSearch, setADSearch, props.searchTable, sortedColumn, sortingOrder, filterDataState, abortController?.current?.signal);
                        handleSetData(props.listName, responsePromises, setData, setHasMoreData, setFreesearchData, data, dataPage, setDataPage);
                    } finally {
                        //ensure that isLoading is set to false even if there is an error
                        setIsLoading(false);
                    }
                }}
                loader={(
                    <p style={{ textAlign: "center" }}>
                        <b>{searchTextHolder.length > 0 && searchTextHolder.trim().length <= 1 && props.listName === "AllLawyerList" ? "Please enter at least 2 characters" : "Loading..."}</b>
                    </p>
                )}
                hasMore={hasMoreData}
                scrollableTarget={props.listContainerID || "ListSection"}
            >
                <Table hover className="sortable">
                    {(props.listName !== "FileList" && props.listName !== "SelectFileList" && props.listName !== "DRFileList" && props.listName !== "TaskList") &&

                        <thead>
                            {props.ListColumns ?
                                props.buildHead(props.ListColumns, handleColumnClick, SESSION_INFO.appProvinceShort, getSortIcon)
                                :
                                props.buildHead(handleColumnClick, getSortIcon)
                            }
                        </thead>
                    }
                    {(props.listName === "FileList" || props.listName === "SelectFileList" || props.listName === "DRFileList" || props.listName === "TaskList") && props.FileListColumns &&
                        <thead>{props.buildHead(props.FileListColumns, handleColumnClick, SESSION_INFO.appProvinceShort, getSortIcon)}</thead>
                    }
                    <tbody>
                        {(props.listName === "FileList" || props.listName === "SelectFileList") ?
                            data.map(props.buildFileListLink(
                                props.FileListColumns,
                                nav,
                                props.showCopyOption,
                                props.setShowCopyOption,
                                props.setCopyAppID,
                                searchText,
                                adSearch.Category === "Archive",
                                handleArchiveFile
                            ))
                            :
                            (() => {
                                switch (props.listName) {
                                    case "MortgageLenderList":
                                        return <>
                                            {data.map(props.buildLenderListLink('Misc', searchText))}
                                            {data.map(props.buildLenderListLink('Mortgage_lender'))}
                                        </>
                                    case "AllMortgageLenderList":
                                        return <>
                                            {data.map((x, index) => props.buildLenderListLink(props.listName, searchText)(x, index))}
                                        </>
                                    case "MortgageBranchList":
                                        return <>
                                            {data.map(props.buildLenderListLink(props.branchEntry, props.listName, 'Misc', searchText))}

                                        </>
                                    case "MortgageLenderSelectFieldList":
                                        return data.map(props.buildLenderListLink(props.setLenderName, props.closeFunc, props.fieldType))
                                    case "DischargeLenderList":
                                        return data.map(props.buildLenderListLink())
                                    case "DRFileList":
                                        return (freesearchData as any).map(props.buildListLink(freesearchData as any[], searchText));
                                    case "TaskList":
                                        return (() => {
                                            if (props.filterComponent?.Activated) {
                                                const activeTasks = (freesearchData as any[]).filter(task => task.output === "1");
                                                const inactiveTasks = (freesearchData as any[]).filter(task => task.output === "0");
                                                const buildLink = props.buildListLink?.(props.defaultListColumns || [], searchText);
                                                
                                                if (!buildLink) return null;
                                                
                                                return (
                                                    <>
                                                        {activeTasks.map((task, index) => buildLink(task, index))}
                                                        {inactiveTasks.length > 0 && (
                                                            <>
                                                                <tr>
                                                                    <td colSpan={1000} style={{ padding: "10px 0 10px 20px", textAlign: "left", backgroundColor: "var(--bg-30)" }}>
                                                                        <b>Inactive Tasks</b>
                                                                    </td>
                                                                </tr>
                                                                {inactiveTasks.map((task, index) => buildLink(task, index))}
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            }
                                            const buildLink = props.buildListLink?.(props.defaultListColumns || [], searchText);
                                            if (!buildLink) return null;
                                            return (freesearchData as any[]).map((task, index) => buildLink(task, index));
                                        })()
                                    default:
                                        // ThirdPartyList
                                        const navObj = { ...thirdPartyNavigateObjs[props.listName] };
                                        const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
                                        navObj.params.id = DataReaderObj.getAppId();
                                        navObj.params.PSM = DataReaderObj.getPSM();
                                        navObj.controller = { ...navObj.controller };
                                        const province = SESSION_INFO.appProvinceShort;
                                        return data.map(props.buildQpickListLink(props.ListColumns, livedataRootContainer?.dataRoot, props.nodeName, navObj, nav, province, searchTextHolder, dispatch, otherListExtraPayload, skipSetThirdPartyData))
                                }
                            })()
                        }
                        {(props.listName === "FileList" || props.listName === "SelectFileList") && !hasMoreData && freesearchData.length > 0 &&
                            <>
                                <tr className="noHover">
                                    <td colSpan={1000} style={{ padding: "0" }}>
                                        <Alert variant="primary"><i>- Starting <b>Expanded Search</b> -</i></Alert>
                                    </td>
                                </tr>
                                {freesearchData.map(props.buildFileListLink(
                                    props.FileListColumns,
                                    nav,
                                    props.showCopyOption,
                                    props.setShowCopyOption,
                                    props.setCopyAppID,
                                    searchText,
                                    adSearch.Category === "Archive",
                                    handleArchiveFile
                                ))}
                            </>
                        }
                    </tbody>
                </Table>
            </InfiniteScroll>

            {!props.notShowEndMessage && (!hasMoreData || showBottomMessage) &&
                EndOfSearchResults()
            }
            
            {showSearchRecommendations &&
                <>
                    <InfiniteScroll next={undefined} hasMore={false} 
                        loader={""} 
                        dataLength={0}
                        endMessage={<></>}
                        >
                            {recommendations.length > 0 ? <>
                            <h4>Suggested:</h4>
                            <Table hover className="sortable">
                                <tbody>
                                {(() => {
                                    var navObj;
                                    if (props.listName === "AllLawyerList") {
                                        navObj = { ...thirdPartyNavigateObjs[props.listName] };
                                        var DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
                                        navObj.params.id = DataReaderObj.getAppId();
                                        navObj.params.PSM = DataReaderObj.getPSM();
                                        navObj.controller = { ...navObj.controller };
                                    }
                                    return recommendations.map(BuildThirdPartyRecommendationsLink(props.ListColumns, livedataRootContainer?.dataRoot, props.nodeName, navObj, nav, SESSION_INFO.appProvinceShort, props.listName, props.branchEntry, searchTextHolder, dispatch, skipSetThirdPartyData));
                                })()}
                                </tbody>
                            </Table>
                            </> 
                            : <></>}
                    </InfiniteScroll>


                </>
            }
        </>

    )

    const getSortIcon = (column: string): JSX.Element => {
        const direction = sortDirections[column] || SortDir.None;
        
        // Special handling for FileList and other similar lists
        if (props.listName === "FileList" || 
            props.listName === "SelectFileList" || 
            props.listName === "DRFileList" ||
            props.listName === "MortgageLenderList" || 
            props.listName === "DischargeLenderList" ||
            props.listName === "TaskList") {
            switch(direction) {
                case SortDir.None:
                    return <i className="fa-solid fa-sort status-secondary-light" style={{opacity: 0.4}}></i>;
                case SortDir.Up:
                    return <i className="fa-duotone fa-sort-down status-secondary"></i>; // Flipped
                case SortDir.Down:
                    return <i className="fa-duotone fa-sort-up status-secondary"></i>; // Flipped
            }
        }

        // Default handling for all other lists
        switch(direction) {
            case SortDir.None:
                return <i className="fa-solid fa-sort status-secondary-light" style={{opacity: 0.4}}></i>;
            case SortDir.Up:
                return <i className="fa-duotone fa-sort-up status-secondary"></i>;
            case SortDir.Down:
                return <i className="fa-duotone fa-sort-down status-secondary"></i>;
        }
    }

    return (
        (!props.as || props.as === "Card") ?
            <Card className="card-list" style={{height:'100%', margin: "0"}}>
                <Card.Header>
                    {HeadPart()}
                </Card.Header>

                {/* {props.listName === "FileList" &&
                    <DismissableAlert
                        variant="primary"
                        msgID="FileListBtnGuide"
                        msg={<>Click the <b>'+'</b> button above to <b>create a new file.</b> Click the <i id="searchOptionBtn2" style={{ cursor: "text" }} className="fa-regular fa-sliders fa-action" /> button above to do an <b>advanced search</b>.</>} />
                } */}

                <Card.Body id={props.listContainerID || "ListSection"} className="ListSection">
                    {BodyPart()}
                </Card.Body>
            </Card>
            :
            <>
                <Modal.Header>
                    {HeadPart()}
                </Modal.Header>
                <Modal.Body id={props.listContainerID || "ListSection"} className="ListSection">
                    {BodyPart()}
                </Modal.Body>
            </>
    )
})


export default List;